/**
 * Allergies service.
 *
 * Wraps the FHIRAllergyIntolerance-backed EMR endpoints so UI components
 * can read and write structured patient allergies without hitting the
 * legacy `patient.medication_allergies` string-array pattern.
 *
 * The older `allergyService.ts` remains in place as the NLM Clinical
 * Tables typeahead (used to suggest allergen names); this service
 * wraps it when building a full allergen picker.
 */

import { api } from './api';

export type AllergyClinicalStatus = 'active' | 'inactive' | 'resolved';
export type AllergyVerificationStatus =
  | 'unconfirmed'
  | 'presumed'
  | 'confirmed'
  | 'refuted'
  | 'entered-in-error';
export type AllergyCriticality = 'low' | 'high' | 'unable-to-assess';
export type AllergyCategory = 'medication' | 'food' | 'environment' | 'biologic';

export interface AllergyReactionManifestation {
  system?: string;
  code?: string;
  display?: string;
}

export interface AllergyReaction {
  substance_code?: string;
  substance_display?: string;
  manifestations?: AllergyReactionManifestation[];
  severity?: 'mild' | 'moderate' | 'severe';
  description?: string;
}

export interface PatientAllergy {
  id: string;
  patient_id: string;
  encounter_id?: string | null;
  clinic_id: string;
  code_system: string;
  code_code: string;
  code_display: string;
  category?: AllergyCategory[] | null;
  allergy_type: 'allergy' | 'intolerance';
  criticality: AllergyCriticality | null;
  clinical_status: AllergyClinicalStatus;
  verification_status: AllergyVerificationStatus;
  onset_date_time?: string | null;
  recorded_date: string;
  recorder_id?: string | null;
  reactions?: AllergyReaction[] | null;
  note?: string | null;
  // Populated when the list endpoint includes associations.
  recorder?: { id: string; first_name: string; last_name: string } | null;
}

export interface CreateAllergyPayload {
  snomedCode?: string;
  allergenCode?: string;
  display?: string;
  allergenDisplay?: string;
  codeSystem?: string;
  type?: 'allergy' | 'intolerance';
  category?: AllergyCategory;
  criticality?: AllergyCriticality;
  clinicalStatus?: AllergyClinicalStatus;
  verificationStatus?: AllergyVerificationStatus;
  onsetDate?: string;
  reactions?: AllergyReaction[];
  note?: string;
  // Encounter linkage — required by the requireClinicalContext middleware.
  encounterId?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'direct_rx';
}

/**
 * Normalize the BE response shape to the FE's snake_case interface.
 *
 * The allergies controller re-maps model fields to camelCase
 * (`code`, `display`, `status`, `verificationStatus`, `recordedDate`).
 * Component code throughout the app reads the original FHIR-style
 * snake_case fields, so we re-map back here in one place.
 */
const allergyFromApi = (a: any): PatientAllergy => ({
  id: a.id,
  patient_id: a.patient_id,
  encounter_id: a.encounter_id ?? null,
  clinic_id: a.clinic_id,
  code_system: a.code_system ?? '',
  code_code: a.code ?? a.code_code,
  code_display: a.display ?? a.code_display,
  category: Array.isArray(a.category)
    ? a.category
    : a.category
      ? [a.category]
      : null,
  allergy_type: (a.type ?? a.allergy_type) as 'allergy' | 'intolerance',
  criticality: a.criticality ?? null,
  clinical_status: (a.status ?? a.clinical_status) as AllergyClinicalStatus,
  verification_status: (a.verificationStatus ?? a.verification_status) as AllergyVerificationStatus,
  onset_date_time: a.onsetDate ?? a.onset_date_time ?? null,
  recorded_date: a.recordedDate ?? a.recorded_date,
  recorder_id: a.recorder_id ?? null,
  reactions: a.reactions ?? null,
  note: a.note ?? null,
  recorder: typeof a.recorder === 'string' || a.recorder == null
    ? null
    : a.recorder,
});

export const allergiesService = {
  async getPatientAllergies(
    clinicId: string,
    patientId: string,
    options?: { activeOnly?: boolean; highRiskOnly?: boolean }
  ): Promise<PatientAllergy[]> {
    const params = new URLSearchParams();
    if (options?.activeOnly) params.append('activeOnly', 'true');
    if (options?.highRiskOnly) params.append('highRiskOnly', 'true');
    const qs = params.toString();
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/allergies${qs ? `?${qs}` : ''}`,
    );
    const rows = response.data?.allergies || [];
    return rows.map(allergyFromApi);
  },

  async createAllergy(
    clinicId: string,
    patientId: string,
    payload: CreateAllergyPayload,
  ): Promise<PatientAllergy> {
    const body: CreateAllergyPayload = {
      ...payload,
      // The clinicalContext middleware demands either an encounter_id
      // or an off_encounter_reason on every clinical write. Default the
      // off-visit pathway to 'chart_correction' since the structured
      // allergy list is typically reconciled outside a specific visit.
      off_encounter_reason:
        payload.off_encounter_reason
        || (payload.encounterId ? undefined : 'chart_correction'),
    };
    const response = await api.post(
      `/emr/${clinicId}/patients/${patientId}/allergies`,
      body,
    );
    return allergyFromApi(response.data || response);
  },

  async updateAllergy(
    clinicId: string,
    allergyId: string,
    updates: Partial<{
      clinicalStatus: AllergyClinicalStatus;
      verificationStatus: AllergyVerificationStatus;
      criticality: AllergyCriticality;
      reactions: AllergyReaction[];
      note: string;
    }>,
  ): Promise<PatientAllergy> {
    const response = await api.put(
      `/emr/${clinicId}/allergies/${allergyId}`,
      updates,
    );
    return allergyFromApi(response.data || response);
  },

  async resolveAllergy(
    clinicId: string,
    allergyId: string,
  ): Promise<PatientAllergy> {
    const response = await api.post(
      `/emr/${clinicId}/allergies/${allergyId}/resolve`,
    );
    return allergyFromApi(response.data || response);
  },

  /**
   * Return the display strings of active medication allergies —
   * the shape legacy UI consumers expected from Patient.medication_allergies.
   */
  async getActiveMedicationAllergyNames(
    clinicId: string,
    patientId: string,
  ): Promise<string[]> {
    const rows = await this.getPatientAllergies(clinicId, patientId, {
      activeOnly: true,
    });
    return rows
      .filter(r => Array.isArray(r.category) && r.category.includes('medication'))
      .map(r => r.code_display)
      .filter(Boolean);
  },

  /**
   * Record that the practitioner reviewed the allergy list during this
   * visit. Mirrors the medication-reconciliation event.
   */
  async recordReconciliation(
    clinicId: string,
    encounterId: string,
    payload: {
      reviewedCount?: number;
      addedCount?: number;
      confirmedCount?: number;
      refutedCount?: number;
      resolvedCount?: number;
      nkda?: boolean;
      notes?: string;
    },
  ): Promise<AllergyReconciliationEvent> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/allergy-reconciliation`,
      payload,
    );
    return response.data?.data || response.data;
  },

  /**
   * Most recent allergy reconciliation event for this encounter, or null
   * if none has been recorded yet. Used to hydrate the modal's notes field.
   */
  async getLatestReconciliation(
    clinicId: string,
    encounterId: string,
  ): Promise<AllergyReconciliationEvent | null> {
    const response = await api.get(
      `/emr/${clinicId}/encounters/${encounterId}/allergy-reconciliation`,
    );
    return response.data ?? null;
  },
};

export interface AllergyReconciliationEvent {
  id: string;
  encounter_id: string | null;
  practitioner_id: string;
  performed_at: string;
  allergies_reviewed_count: number;
  allergies_added_count: number;
  allergies_confirmed_count: number;
  allergies_refuted_count: number;
  allergies_resolved_count: number;
  nkda: boolean;
  notes: string | null;
}

export default allergiesService;
